import React, { createContext, useContext, useState, useEffect } from "react";
import apiService, { API_ENDPOINTS } from "../utils/apiInterceptor";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const savedUser = localStorage.getItem("user");
    const savedToken = localStorage.getItem("token");

    if (savedUser && savedToken) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const result = await apiService.post(
        API_ENDPOINTS.AUTH.LOGIN,
        {
          email,
          password,
        },
        { includeAuth: false }
      ); // Don't include auth header for login

      if (
        result.success &&
        result.data &&
        result.data.user &&
        result.data.accessToken
      ) {
        const userData = result.data.user;
        const token = result.data.accessToken;
        const refreshToken = result.data.refreshToken;

        // Store user data and token
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("token", token);
        localStorage.setItem("refreshToken", refreshToken);
        setUser(userData);

        return userData;
      } else {
        throw new Error(result.message || "Invalid response from server");
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call logout API to invalidate token on server
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error("Logout API error:", error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local storage and state
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    }
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const result = await apiService.post(
        API_ENDPOINTS.AUTH.REFRESH,
        {
          refreshToken,
        },
        { includeAuth: false }
      );

      if (result.success && result.data && result.data.accessToken) {
        localStorage.setItem("token", result.data.accessToken);
        localStorage.setItem("refreshToken", result.data.refreshToken);
        return result.data.accessToken;
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      // If refresh fails, logout user
      logout();
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const result = await apiService.put(
        API_ENDPOINTS.AUTH.PROFILE,
        profileData
      );

      if (result.success && result.data) {
        const updatedUser = { ...user, ...result.data };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return updatedUser;
      } else {
        throw new Error(result.message || "Profile update failed");
      }
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const result = await apiService.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
      });

      if (!result.success) {
        throw new Error(result.message || "Password change failed");
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    refreshToken,
    updateProfile,
    changePassword,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
